import { RasterTileStore } from './tiles';

export type RasterDeformSettings =
  | { mode: 'liquify'; brush: 'warp' | 'pucker' | 'bloat'; centerX: number; centerY: number; radius: number; deltaX: number; deltaY: number; strength: number }
  | { mode: 'puppet'; pinX: number; pinY: number; deltaX: number; deltaY: number; radius: number }
  | { mode: 'warp-perspective'; bendX: number; bendY: number; perspectiveX: number; perspectiveY: number };

interface Pixel {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

function pixel(store: RasterTileStore, x: number, y: number): Pixel {
  if (x < 0 || y < 0 || x >= store.width || y >= store.height) return { red: 0, green: 0, blue: 0, alpha: 0 };
  const integerX = Math.floor(x);
  const integerY = Math.floor(y);
  const tileX = Math.floor(integerX / store.tileSize);
  const tileY = Math.floor(integerY / store.tileSize);
  const tile = store.getTile(tileX, tileY);
  if (!tile) return { red: 0, green: 0, blue: 0, alpha: 0 };
  const dimensions = store.tileDimensions(tileX, tileY);
  const localX = integerX - tileX * store.tileSize;
  const localY = integerY - tileY * store.tileSize;
  const index = (localY * dimensions.width + localX) * 4;
  return { red: tile[index] ?? 0, green: tile[index + 1] ?? 0, blue: tile[index + 2] ?? 0, alpha: tile[index + 3] ?? 0 };
}

function sample(store: RasterTileStore, x: number, y: number): Pixel {
  const left = Math.floor(x);
  const top = Math.floor(y);
  const amountX = x - left;
  const amountY = y - top;
  const a = pixel(store, left, top);
  const b = pixel(store, left + 1, top);
  const c = pixel(store, left, top + 1);
  const d = pixel(store, left + 1, top + 1);
  const mix = (first: number, second: number, amount: number) => first + (second - first) * amount;
  const channel = (key: keyof Pixel) => Math.round(mix(mix(a[key], b[key], amountX), mix(c[key], d[key], amountX), amountY));
  return { red: channel('red'), green: channel('green'), blue: channel('blue'), alpha: channel('alpha') };
}

export function inverseDeformPoint(width: number, height: number, x: number, y: number, settings: RasterDeformSettings): { x: number; y: number } {
  if (settings.mode === 'liquify') {
    const dx = x - settings.centerX;
    const dy = y - settings.centerY;
    const distance = Math.hypot(dx, dy);
    if (distance >= settings.radius || settings.radius <= 0) return { x, y };
    const falloff = (1 - (distance / settings.radius) ** 2) ** 2 * Math.max(0, Math.min(1, settings.strength));
    if (settings.brush === 'warp') return { x: x - settings.deltaX * falloff, y: y - settings.deltaY * falloff };
    const scale = settings.brush === 'pucker' ? 1 + falloff * 0.75 : Math.max(0.1, 1 - falloff * 0.65);
    return { x: settings.centerX + dx * scale, y: settings.centerY + dy * scale };
  }
  if (settings.mode === 'puppet') {
    const distance = Math.hypot(x - settings.pinX, y - settings.pinY);
    const weight = 1 / (1 + (distance / Math.max(1, settings.radius)) ** 2 * 8);
    const edge = Math.min(x, y, width - 1 - x, height - 1 - y) / Math.max(1, Math.min(width, height) * 0.15);
    const anchoredWeight = weight * Math.max(0, Math.min(1, edge));
    return { x: x - settings.deltaX * anchoredWeight, y: y - settings.deltaY * anchoredWeight };
  }
  const u = width <= 1 ? 0 : x / (width - 1);
  const v = height <= 1 ? 0 : y / (height - 1);
  const perspectiveScaleX = Math.max(0.2, 1 + settings.perspectiveX * (v - 0.5) * 1.5);
  const perspectiveScaleY = Math.max(0.2, 1 + settings.perspectiveY * (u - 0.5) * 1.5);
  const sourceU = 0.5 + (u - 0.5) / perspectiveScaleX - settings.bendX * Math.sin(Math.PI * v) * (1 - (2 * u - 1) ** 2) * 0.2;
  const sourceV = 0.5 + (v - 0.5) / perspectiveScaleY - settings.bendY * Math.sin(Math.PI * u) * (1 - (2 * v - 1) ** 2) * 0.2;
  return { x: sourceU * (width - 1), y: sourceV * (height - 1) };
}

export function deformRaster(source: RasterTileStore, settings: RasterDeformSettings, maxPixels = 16_000_000): RasterTileStore {
  if (source.width * source.height > maxPixels) throw new Error('Deformation is limited to 16 megapixels per layer in this release.');
  const output = new RasterTileStore(source.width, source.height, source.tileSize);
  for (let y = 0; y < source.height; y += 1) for (let x = 0; x < source.width; x += 1) {
    const mapped = inverseDeformPoint(source.width, source.height, x, y, settings);
    const value = sample(source, mapped.x, mapped.y);
    if (value.alpha === 0) continue;
    const tileX = Math.floor(x / output.tileSize);
    const tileY = Math.floor(y / output.tileSize);
    const tile = output.getTile(tileX, tileY, true);
    if (!tile) continue;
    const dimensions = output.tileDimensions(tileX, tileY);
    const localX = x - tileX * output.tileSize;
    const localY = y - tileY * output.tileSize;
    const index = (localY * dimensions.width + localX) * 4;
    tile.set([value.red, value.green, value.blue, value.alpha], index);
  }
  return output;
}
